<template>
  <div class="container">
    <div class="row justify-content-between align-items-center mb-3 mt-4">
      <div class="col-auto">
        <h1>Allocation</h1>
      </div>
      <div class="col-auto d-flex align-items-center">
        <div class="input-group me-2">
          <span class="input-group-text">Start from company ID</span>
          <input
            type="number"
            class="form-control"
            v-model="startFromCompanyId"
            placeholder="Optional"
            :disabled="isLoading"
          />
        </div>
        <button
          class="btn btn-primary"
          @click="triggerAllocationRun"
          :disabled="isLoading"
        >
          <span
            v-if="isLoading"
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          Trigger Allocation Run
        </button>
      </div>
    </div>

    <div v-if="allocationRunId" class="row mb-4">
      <div class="col">
        <div class="alert alert-info d-flex align-items-center">
          <div
            class="spinner-border spinner-border-sm me-2"
            role="status"
            v-if="isRefreshing"
          ></div>
          <div>
            <strong>Allocation Run ID:</strong>
            {{ decodeURIComponent(allocationRunId) }}
            <div class="small">
              Refreshing data every 30 seconds. Last updated: {{ lastUpdated }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="allocationRunId">
      <div class="col">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Portfolio Movements</h5>
          </div>
          <div class="card-body p-0">
            <LoadingIndicatorComponent
              v-if="isLoading && !movements.length"
              message="Loading movements..."
            />
            <div v-else-if="!movements.length" class="text-center p-5">
              <p>No movements found for this allocation run.</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Certificate</th>
                    <th>From Portfolio</th>
                    <th>To Portfolio</th>
                    <th>Company</th>
                    <th>Count</th>
                    <th>Reason</th>
                    <th>Moved At</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="movement in movements" :key="movement.id">
                    <td>{{ movement.id }}</td>
                    <td>{{ movement.certificate_id }}</td>
                    <td>{{ movement.from_portfolio_name }}</td>
                    <td>{{ movement.to_portfolio_name }}</td>
                    <td>{{ movement.company_name }}</td>
                    <td>{{ movement.count }}</td>
                    <td>{{ movement.reason }}</td>
                    <td>{{ formatDate(movement.moved_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useIntervalFn } from "@vueuse/core";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const route = useRoute();
const router = useRouter();
const { globalErrorHandler } = useGlobalHandlers();

const allocationRunId = ref<string | null>(null);
const movements = ref<any[]>([]);
const isLoading = ref(false);
const isRefreshing = ref(false);
const lastUpdatedTime = ref<Date | null>(null);
const startFromCompanyId = ref<number | null>(null);
const { resume: startRefreshInterval } = useIntervalFn(
  () => fetchMovements(true),
  30000,
  { immediate: false }
);

const lastUpdated = computed(() => {
  if (!lastUpdatedTime.value) return "";
  return lastUpdatedTime.value.toLocaleTimeString();
});

/**
 * Trigger a new allocation run
 * @param startFromCompanyId Optional company ID to start allocation from
 * @returns Promise with the allocation run ID
 */
async function triggerAllocation(startFromCompanyId?: number): Promise<{
  allocation_run_id: string;
}> {
  const response = await goSolidApi.post("/api/admin/allocation/trigger", {
    start_from_company_id: startFromCompanyId ?? null,
  });
  return response.data;
}

/**
 * Get portfolio movements for a specific allocation run
 * @param allocationRunId The ID of the allocation run to get movements for
 * @returns Promise with the list of portfolio movements
 */
async function getAllocationMovements(
  allocationRunId: string
): Promise<{ movements: any[] }> {
  const response = await goSolidApi.get("/api/admin/allocation/movements", {
    params: { allocation_run_id: allocationRunId },
  });
  return response.data;
}

onMounted(() => {
  // Check if allocation_run_id is in the URL
  const urlAllocationRunId = route.query.allocation_run_id as string;
  if (urlAllocationRunId) {
    allocationRunId.value = urlAllocationRunId;
    fetchMovements();
    startRefreshInterval();
  }
});

async function triggerAllocationRun() {
  try {
    isLoading.value = true;
    // Convert to number or undefined (not null) to match API parameter type
    const companyId = startFromCompanyId.value
      ? Number(startFromCompanyId.value)
      : undefined;
    const response = await triggerAllocation(companyId);
    allocationRunId.value = response.allocation_run_id;

    // Update URL with allocation_run_id
    router.replace({
      query: { ...route.query, allocation_run_id: allocationRunId.value },
    });

    // Fetch movements and start refresh interval
    await fetchMovements();
    startRefreshInterval();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
}

async function fetchMovements(isRefresh = false) {
  if (!allocationRunId.value) return;

  try {
    if (isRefresh) {
      isRefreshing.value = true;
    } else {
      isLoading.value = true;
    }

    const response = await getAllocationMovements(allocationRunId.value);
    movements.value = response.movements || [];
    lastUpdatedTime.value = new Date();
  } catch (error) {
    if (!isRefresh) {
      globalErrorHandler(error);
    }
  } finally {
    isRefreshing.value = false;
    isLoading.value = false;
  }
}

function formatDate(dateString: string): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString();
}
</script>

<style scoped lang="scss">
.table th,
.table td {
  vertical-align: middle;
}
</style>
